import React, { useState } from 'react';

import { Box, Link, Stack, Typography } from '@mui/material';

import { useTranslate } from 'src/locales';

// ----------------------------------------------------------------------

const PREVIEW_LENGTH = 120;

/**
 * Notes are free text and can run to several paragraphs, so a feed row shows a preview rather than
 * the whole thing. Line breaks collapse because the preview is a single line - the full text keeps
 * its formatting once expanded.
 */
export function truncateNoteText(text: string, limit: number = PREVIEW_LENGTH): string {
  const collapsed = (text ?? '').replace(/\s+/g, ' ').trim();

  if (collapsed.length <= limit) {
    return collapsed;
  }

  return `${collapsed.slice(0, limit)}…`;
}

// ----------------------------------------------------------------------

interface NoteEditActivityContentProps {
  previousText: string;
  newText: string;
}

/**
 * JT1-1562: the before and after of an edited note. Both versions ship with the feed payload in
 * metadata.comment - unlike the communication content next door there is nothing to fetch, since
 * the activity entry is itself the only record of what the note used to say.
 */
const NoteEditActivityContent: React.FC<NoteEditActivityContentProps> = ({ previousText, newText }) => {
  const { t } = useTranslate();
  const [open, setOpen] = useState(false);

  const previousPreview = truncateNoteText(previousText);
  const newPreview = truncateNoteText(newText);

  // nothing was hidden, so a toggle would only promise detail the row is already showing.
  const isTruncated = previousPreview !== previousText.trim() || newPreview !== newText.trim();

  const renderVersion = (label: string, text: string) => (
    <Stack direction="row" spacing={0.5} alignItems="baseline">
      <Typography component="span" variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography
        component="span"
        variant="caption"
        color="text.primary"
        sx={{ whiteSpace: open ? 'pre-wrap' : 'normal' }}
      >
        {text}
      </Typography>
    </Stack>
  );

  return (
    <Box sx={{ mt: 0.5 }}>
      {/* the collapsed half is swapped out rather than hidden - a Collapse keeps its children
          mounted, which would leave the whole note sitting in the DOM at zero height */}
      <Stack spacing={0.25}>
        {renderVersion(t('activities.feed.note.before'), open ? previousText : previousPreview)}
        {renderVersion(t('activities.feed.note.after'), open ? newText : newPreview)}
      </Stack>

      {isTruncated && (
        <Link
          component="button"
          type="button"
          variant="caption"
          onClick={() => setOpen((previous) => !previous)}
        >
          {open ? t('activities.feed.content.hide') : t('activities.feed.content.show')}
        </Link>
      )}
    </Box>
  );
};

export default NoteEditActivityContent;
